import { useEffect, useState } from "react";
import {
  categoriseMessages,
  getPromoMessaging,
  trimMessageTerms,
} from "../../utils/product/getPromoMessaging";
import { dispatchCustomEvent } from "../../utils/customEvent/dispatchCustomEvent";
import {
  NonPromotionalMessage,
  PromotionalMessage,
} from "../../types/prodcatBaseTypes";
import { SpecialOffersDrawer } from "./components/SpecialOffersDrawer/SpecialOffersDrawer";
import { SpecialOffersListItem } from "./components/SpecialOffersListItem/SpecialOffersListItem";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";

import {
  getCookieDrawerOpen,
  setCookieDrawerOpen,
} from "./utils/drawerOpenCookieHelper";

type Props = {
  /**
   * An array of Message objects.
   */
  messages: (NonPromotionalMessage | PromotionalMessage)[];
  isBlackOverride: boolean;
  prodCatEndpoint?: string;
  prodCatApiKey?: string;
  basketEndpoint?: string;
  specialOffersVariant?: string;
  runtimeConfig?: Record<string, any>;
  experiencesRawObject?: Experiences;
  applyNewDrawerUI?: boolean;
};

export const SpecialOffersUI = ({
  messages,
  isBlackOverride = false,
  prodCatEndpoint,
  prodCatApiKey,
  basketEndpoint,
  specialOffersVariant,
  runtimeConfig,
  experiencesRawObject = [],
  applyNewDrawerUI = false,
}: Props) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const { promoDrawerMessages } = getPromoMessaging(messages);
  const { monetateId } = useMonetateId();

  useEffect(() => {
    const listener = () => {
      setDrawerIsOpen(true);
    };

    document.addEventListener("TRADE_IN_READ_MORE_EVENT", listener);

    return () => {
      document.removeEventListener("TRADE_IN_READ_MORE_EVENT", listener);
    };
  });

  useEffect(() => {
    const isCustomEvent = (event: Event): event is CustomEvent => {
      return "detail" in event;
    };

    document.body.addEventListener("dataStateChange", (e: Event) => {
      if (isCustomEvent(e)) {
        if (!e.detail) {
          return;
        }

        const action = e.detail.action;
        const wasOffersDrawerOpened = getCookieDrawerOpen();

        if (action === "addtobasketsuccess" && wasOffersDrawerOpened) {
          dispatchMonetateQEvent("ATB_OFFERS_DRAWER_OPENED");
        }
      } else {
        return;
      }
    });
  }, []);

  const dispatchSpecialOffersDrawerEvent = () => {
    dispatchCustomEvent("TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT");
  };

  const closeDrawerAndOpenTradeInModal = () => {
    setDrawerIsOpen(false);
    dispatchSpecialOffersDrawerEvent();
  };

  const openDrawer = () => {
    setCookieDrawerOpen();
    dispatchMonetateQEvent("SPECIAL_OFFERS_DRAWER_OPEN");
    setDrawerIsOpen(true);

    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject,
      "special-offers-ui-update",
      "Control",
      monetateId,
    );
  };

  if (promoDrawerMessages.length === 0) {
    return null;
  }

  const promotions = promoDrawerMessages.map((promotion) =>
    trimMessageTerms(promotion),
  );

  const messagesWithCategoryAndType = categoriseMessages(promotions);

  return (
    <>
      <SpecialOffersListItem
        promotions={messagesWithCategoryAndType}
        onClick={openDrawer}
        isBlackOverride={isBlackOverride}
        specialOffersVariant={specialOffersVariant}
      />
      <SpecialOffersDrawer
        promotions={messagesWithCategoryAndType}
        drawerIsOpen={drawerIsOpen}
        onClose={() => {
          setDrawerIsOpen(false);
        }}
        onPromotionClose={closeDrawerAndOpenTradeInModal}
        isBlackOverride={isBlackOverride}
        prodCatEndpoint={prodCatEndpoint}
        prodCatApiKey={prodCatApiKey}
        basketEndpoint={basketEndpoint}
        specialOffersVariant={specialOffersVariant}
        applyNewDrawerUI={applyNewDrawerUI}
      />
    </>
  );
};
